import { useEffect, useState } from 'react';
import { Button, Surface, Text } from 'react-native-paper';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { getDriverWorkContext, type DriverWorkContext } from '../api/apiService';

/** Keep actionable server warnings on home; routine identity lives in the sidebar. */
export function WorkContextCard({ sessionActive }: { sessionActive?: boolean }) {
  const router = useRouter();
  const { t } = useTranslation('workCompany');
  const [context, setContext] = useState<DriverWorkContext | null>(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let live = true;
    let inFlight = false;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const response = await getDriverWorkContext();
        if (!live) return;
        if (!response.ok || !response.data) { setError(true); return; }
        setContext(response.data as DriverWorkContext);
        setError(false);
      } catch {
        if (live) setError(true);
      } finally {
        inFlight = false;
      }
    };
    void load();
    const interval = setInterval(() => void load(), 30000);
    return () => { live = false; clearInterval(interval); };
  }, [sessionActive, retry]);
  const historicalCompanyMissing = context?.open_session_context && !context.open_session_context.company_name;
  if (!error && context && !context.pending_reason && !historicalCompanyMissing) return null;
  return <Surface testID="work-context-card" elevation={0} style={{ padding: 12, gap: 8 }}>
    {error ? <>
      <Text accessibilityRole="alert">{t('retry', 'Unable to validate the work company. Retry or contact support.')}</Text>
      <Button testID="work-context-retry" onPress={() => setRetry(value => value + 1)}>
        {t('retryAction', 'Retry')}
      </Button>
    </> : context ? <>
      {historicalCompanyMissing && <Text accessibilityRole="alert">
        {t('historicalCompanyMissing', 'No company recorded for this open session')}
      </Text>}
      {context.pending_reason && <Text accessibilityRole="alert">
        {t('situation', 'Situation')}: {t(`pending.${context.pending_reason}`, 'Retry or contact support.')}
      </Text>}
    </>
      : <Text>{t('loading', 'Loading work context')}</Text>}
    {!error && context?.pending_reason === 'work_company_required' && !context.open_session_context &&
      <Button compact testID="work-context-change" onPress={() => router.push('/dashboard/work-company')}>
        {t('identify', 'Identify work company')}
      </Button>}
  </Surface>;
}
